/**
 * Утверждение бюджетных справочников сезона.
 *
 * UAT founder 20.08 (прод v1.1746): «а как справочники утверждать?» — хаб
 * сезона уже показывал счётчик «0 из 6 утверждены» (`SeasonCard.catalogs_approved`,
 * `src/lib/planning/api.ts`), но механики утверждения не было ни на одном
 * экране. Бэк при этом нёс полный жизненный цикл ещё с ADR-053/SA-03
 * (`services/season_planning.py::approve_catalog`,
 * `api/v1/season_planning.py::GET/POST .../catalogs*`) — счётчик просто было
 * нечем двигать. Этот модуль — тонкий фронт-слой над готовым бэком.
 *
 * `catalog_kind` совпадает 1:1 со вкладками бюджетных справочников
 * (см. `TAB_CATALOG_KIND` в `budget-catalogs/index.tsx`):
 *   tmc_prices → «ТМЦ и плановые цены» (всегда уровень компании — закупки
 *     ведутся централизованно, ADR-065 порция 3-4);
 *   operations_rates → «Расценки работ и топливо»;
 *   outsource → «Аутсорс»;
 *   sale_prices → «Готовая продукция».
 * `machinery` и `land` в бюджетный справочник НЕ входят (парк техники и поля —
 * зона domain-operations-machinery / domain-fields-crops), утверждаются на
 * своих экранах, этот модуль их не трогает.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { describeApiError } from '@/lib/apiError';
import { toast } from '@/lib/toast';

export type CatalogStatus = 'empty' | 'in_progress' | 'approved' | 'revised';

export interface SeasonCatalog {
  catalog_kind: string;
  title: string;
  status: CatalogStatus;
  status_title: string;
  version: number;
  level: 'company' | 'farm';
  farm_id: string | null;
  approved_at: string | null;
  approved_by_user_id: string | null;
  approved_by_name: string | null;
  revised_at: string | null;
  completeness_pct: number | null;
  completeness_note: string | null;
}

interface CatalogApproveResponse {
  catalog: SeasonCatalog;
  stale_calculations: unknown[];
}

const seasonCatalogsKey = (seasonId: string | null, farmId: string | null) =>
  ['planning', 'season-catalogs', seasonId ?? 'none', farmId ?? 'company'] as const;

export function useSeasonCatalogs(seasonId: string | null, farmId: string | null = null) {
  return useQuery<SeasonCatalog[], Error>({
    queryKey: seasonCatalogsKey(seasonId, farmId),
    queryFn: () => {
      const qs = farmId ? `?farm_id=${encodeURIComponent(farmId)}` : '';
      return api.get<SeasonCatalog[]>(`/api/v1/planning/seasons/${seasonId}/catalogs${qs}`);
    },
    enabled: !!seasonId,
    staleTime: 30_000,
  });
}

/** Найти карточку статуса по виду справочника — хелпер для экрана секции. */
export function findCatalog(
  catalogs: SeasonCatalog[] | undefined,
  catalogKind: string,
): SeasonCatalog | undefined {
  return catalogs?.find((c) => c.catalog_kind === catalogKind);
}

function useInvalidateSeasonCatalogs(seasonId: string | null) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['planning', 'season-catalogs', seasonId ?? 'none'] });
    // Хаб сезонов держит агрегат «N из 6 утверждены» отдельным запросом
    // (`SeasonCard.catalogs_approved`) — без этого счётчик хаба не ожил бы
    // сразу после утверждения на экране справочника.
    void qc.invalidateQueries({ queryKey: ['planning', 'season-cards'] });
  };
}

function notifyFailure(err: unknown): void {
  toast.error(describeApiError(err));
}

export interface CatalogActionInput {
  catalogKind: string;
  farmId: string | null;
}

export function useApproveCatalog(seasonId: string | null) {
  const invalidate = useInvalidateSeasonCatalogs(seasonId);
  return useMutation<SeasonCatalog, Error, CatalogActionInput>({
    mutationFn: ({ catalogKind, farmId }) => {
      const qs = farmId ? `?farm_id=${encodeURIComponent(farmId)}` : '';
      return api
        .post<CatalogApproveResponse>(
          `/api/v1/planning/seasons/${seasonId}/catalogs/${catalogKind}/approve${qs}`,
          {},
        )
        .then((r) => r.catalog);
    },
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

export function useUnapproveCatalog(seasonId: string | null) {
  const invalidate = useInvalidateSeasonCatalogs(seasonId);
  return useMutation<SeasonCatalog, Error, CatalogActionInput>({
    mutationFn: ({ catalogKind, farmId }) => {
      const qs = farmId ? `?farm_id=${encodeURIComponent(farmId)}` : '';
      return api.post<SeasonCatalog>(
        `/api/v1/planning/seasons/${seasonId}/catalogs/${catalogKind}/unapprove${qs}`,
        {},
      );
    },
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}
